<template>
  <SoAdminPage title="DCA management">
    <template #subtitle>
      <span class="d-flex align-center flex-wrap ga-3">
        <span>
          Split of new placements between agencies, by segment and DCA placement
        </span>
        <DcaAllocationFlagBadges />
      </span>
    </template>

    <!--
      An unreadable config must not render as twelve empty cells. Every one of them would
      invite the user to add a split over a config that already exists, and saving it
      would overwrite that config with a guess - so a failed read replaces the matrix
      rather than sitting above a matrix that stays editable, matching how the other
      admin tables handle a failed read.
    -->
    <SoAlert
      v-if="hasConfigError"
      type="error"
      title="Failed to load the allocation config"
      data-testid="dca-allocation-config-error"
    >
      This screen cannot show what is currently configured, so nothing can be
      edited from here until it loads.
      <div class="mt-3">
        <SoButton
          type="secondary"
          title="Try again"
          :loading="isLoading"
          data-testid="dca-allocation-config-retry"
          @click="config.refetch()"
        />
      </div>
    </SoAlert>

    <v-row v-else>
      <v-col cols="12" md="8">
        <DcaAllocationLegend />
        <DcaAllocationMatrix
          :cells="cells"
          :selection="selection"
          :loading="isLoading"
          @select="onSelect"
        />
        <DcaLitigationFileSummary class="mt-6" />
      </v-col>
      <v-col cols="12" md="4">
        <DcaAllocationPanel
          v-if="selection"
          :segment-label="selection.row.label"
          :placement-label="placementLabelText"
          :allocations="selectedAllocations"
          :saving="isSaving"
          :changes="changes"
          :history-loading="isHistoryLoading"
          :history-error="hasHistoryError"
          @close="selection = null"
          @save="onSave"
        />
        <p
          v-else
          class="text-caption text-medium-emphasis"
          data-testid="dca-allocation-nothing-selected"
        >
          Select a step to see its split and edit it.
        </p>
      </v-col>
    </v-row>
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import DcaAllocationMatrix, {
    ISelection,
  } from "./components/DcaAllocationMatrix.vue"
  import DcaAllocationPanel from "./components/DcaAllocationPanel.vue"
  import DcaAllocationLegend from "./components/DcaAllocationLegend.vue"
  import DcaAllocationFlagBadges from "./components/DcaAllocationFlagBadges.vue"
  import DcaLitigationFileSummary from "./components/DcaLitigationFileSummary.vue"
  import {
    DcaAllocationService,
    findCell,
  } from "@/services/DcaAllocationService"
  import { DcaAllocationMutator } from "@/mutators/dcaAllocation/DcaAllocationMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorReporter } from "@/services/ErrorService"
  import { placementLabel } from "./dcaAllocation.config"
  import { savedMessage } from "./dcaAllocationSchedule"
  import {
    IDcaAllocation,
    IDcaAllocationCell,
    IDcaAllocationChange,
  } from "@/models/dcaAllocation/dcaAllocation.types"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoAlert,
      SoButton,
      DcaAllocationMatrix,
      DcaAllocationPanel,
      DcaAllocationLegend,
      DcaAllocationFlagBadges,
      DcaLitigationFileSummary,
    },
    data() {
      return {
        selection: null as ISelection | null,
        isSaving: false,
        changes: [] as IDcaAllocationChange[],
        isHistoryLoading: false,
        hasHistoryError: false,
      }
    },
    computed: {
      config() {
        return DcaAllocationService.getAllocationConfig()
      },
      cells(): IDcaAllocationCell[] {
        return this.config.data ?? []
      },
      isLoading(): boolean {
        return this.config.isFetching
      },
      hasConfigError(): boolean {
        return this.config.isError
      },
      placementLabelText(): string {
        if (!this.selection) {
          return ""
        }
        return placementLabel(this.selection.placementNumber)
      },
      selectedAllocations(): IDcaAllocation[] {
        if (!this.selection) {
          return []
        }
        return (
          findCell(
            this.cells,
            this.selection.row.occupier,
            this.selection.row.accountState,
            this.selection.placementNumber,
          )?.allocations ?? []
        )
      },
    },
    methods: {
      onSelect(selection: ISelection) {
        this.selection = selection
        this.loadHistory()
      },
      /**
       * Reloaded on selection and after every save, because a save is exactly what adds
       * to it - leaving the previous cell's history on screen would attribute one cell's
       * changes to another.
       */
      async loadHistory() {
        if (!this.selection) {
          this.changes = []
          return
        }
        const { row, placementNumber } = this.selection
        this.changes = []
        this.hasHistoryError = false
        this.isHistoryLoading = true
        try {
          this.changes = await DcaAllocationService.fetchAllocationHistory(
            row.occupier,
            row.accountState,
            placementNumber,
          )
        } catch (error: unknown) {
          errorReporter.report(error)
          this.hasHistoryError = true
        } finally {
          this.isHistoryLoading = false
        }
      },
      async onSave(allocations: IDcaAllocation[]) {
        if (!this.selection) {
          return
        }
        const snackbar = useSnackbarStore()
        const { row, placementNumber } = this.selection
        this.isSaving = true
        try {
          await DcaAllocationMutator.save({
            occupier: row.occupier,
            accountState: row.accountState,
            placementNumber,
            allocations,
          })
          await this.loadHistory()
          snackbar.addSuccess(
            savedMessage(
              allocations.length === 0
                ? `${row.label} ${this.placementLabelText} retired.`
                : `${row.label} ${this.placementLabelText} split saved.`,
            ),
          )
        } catch (error: unknown) {
          errorReporter.report(error)
          snackbar.addError(`Could not save the split for ${row.label}.`)
        } finally {
          this.isSaving = false
        }
      },
    },
  })
</script>

<style scoped>
  /*
   * Vuetify sets font-family per typography class, so a `.text-caption` renders in its
   * Roboto no matter what `body` says - which put most of the small print on this screen
   * off brand. `inherit` walks back up to the Founders Grotesk on `body`.
   *
   * Scoped and pierced with :deep so it reaches the matrix, panel and legend and stops
   * there. The same gap exists across the rest of Nova - `.text-caption` and the
   * subtitles are absent from the brand-font rule in main.css - but fixing it there
   * changes type on ~33 elements in 17 unrelated files, which is a design decision and
   * its own change, not a side effect of a DCA ticket.
   */
  :deep(.text-caption),
  :deep(.text-subtitle-1),
  :deep(.text-subtitle-2) {
    font-family: inherit;
  }
</style>
